import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, MessageSquare } from 'lucide-react';

/**
 * Threaded negotiation dialog for a specific price_response.
 * - viewerRole: 'admin' | 'subcontractor' — controls labels & alignment
 * - prId / responseId — target thread
 * - lang: 'fr' | 'en'
 * - initialPrice / currency: shown as reference (the original offer)
 * - subLabel / clientLabel: optional context header
 * - onChange: called after each successful post (parent can refetch)
 */
export default function NegotiationDialog({
  open, onOpenChange, viewerRole, prId, responseId,
  lang = 'fr', initialPrice = null, currency = 'EUR',
  subLabel = '', clientLabel = '', onChange,
}) {
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [counterPrice, setCounterPrice] = useState('');
  const scrollRef = useRef(null);

  const isAdmin = viewerRole === 'admin' || viewerRole === 'commercial';
  const fr = lang === 'fr';

  const load = useCallback(async () => {
    if (!prId || !responseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/price-requests/${prId}/responses/${responseId}/negotiations`);
      setThread(data || []);
    } catch (err) {
      // Silently allow empty thread on 403/404 — dialog stays usable if backend has newer data
      setThread([]);
    } finally {
      setLoading(false);
    }
  }, [prId, responseId]);

  useEffect(() => {
    if (open) {
      load();
      setMessage('');
      setCounterPrice('');
    }
  }, [open, load]);

  useEffect(() => {
    // auto-scroll to bottom when thread grows
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread.length]);

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(fr ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };
  const fmtCur = (v, c = 'EUR') => {
    if (v == null || v === '') return '';
    try {
      return new Intl.NumberFormat(fr ? 'fr-FR' : 'en-GB', { style: 'currency', currency: c || 'EUR' }).format(Number(v));
    } catch { return `${v} ${c || ''}`; }
  };

  const submit = async () => {
    const msg = (message || '').trim();
    const cp = counterPrice === '' ? null : parseFloat(counterPrice);
    if (!msg && cp == null) {
      toast.error(fr ? 'Message ou contre-proposition requis' : 'Message or counter-offer required');
      return;
    }
    if (cp != null && (isNaN(cp) || cp < 0)) {
      toast.error(fr ? 'Prix invalide' : 'Invalid price');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/price-requests/${prId}/responses/${responseId}/negotiate`,
        { message: msg, counter_price: cp, currency: currency || 'EUR' });
      setThread(t => [...t, data]);
      setMessage('');
      setCounterPrice('');
      toast.success(fr ? '📨 Envoyé' : '📨 Sent');
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    } finally {
      setSending(false);
    }
  };

  const header = (
    <>
      <DialogTitle className="flex items-center gap-2 text-zinc-100">
        <MessageSquare className="w-4 h-4 text-amber-400" />
        {fr ? 'Négociation' : 'Negotiation'}
        {subLabel && <span className="text-sm text-zinc-400 font-normal">· {subLabel}</span>}
      </DialogTitle>
      <DialogDescription className="text-xs text-zinc-500">
        {fr ? 'Proposition / contre-proposition entre ProdLink et le sous-traitant.' : 'Proposal and counter-offer between ProdLink and the subcontractor.'}
        {initialPrice != null && (
          <span className="ml-1 text-zinc-400">{fr ? 'Offre initiale' : 'Initial offer'}: <span className="text-amber-400 font-mono">{fmtCur(initialPrice, currency)}</span></span>
        )}
        {clientLabel && <span className="ml-1 text-zinc-500">· {clientLabel}</span>}
      </DialogDescription>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="negotiation-dialog">
        <DialogHeader>{header}</DialogHeader>

        <div ref={scrollRef} className="max-h-[50vh] overflow-y-auto space-y-2 py-2 pr-1" data-testid="negotiation-thread">
          {loading && <div className="text-center py-6 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />{fr ? 'Chargement…' : 'Loading…'}</div>}
          {!loading && thread.length === 0 && (
            <div className="text-center py-6 text-zinc-500 text-sm">
              {fr ? 'Aucun échange pour l’instant. Envoyez la première proposition ci-dessous.' : 'No exchanges yet. Send the first proposal below.'}
            </div>
          )}
          {thread.map((e) => {
            const mine = (isAdmin && e.from_role === 'admin') || (!isAdmin && e.from_role === 'subcontractor');
            return (
              <div key={e.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`negotiation-entry-${e.id}`}>
                <div className={`max-w-[80%] rounded-sm border p-2.5 text-sm ${mine
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-100'
                  : e.from_role === 'admin'
                    ? 'bg-blue-500/10 border-blue-500/30 text-blue-100'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
                      {e.from_role === 'admin' ? (fr ? 'ProdLink' : 'ProdLink') : (e.from_name || (fr ? 'Sous-traitant' : 'Subcontractor'))}
                    </span>
                    <span className="text-[10px] text-zinc-500">· {fmtDate(e.at)}</span>
                  </div>
                  {e.message && <div className="whitespace-pre-wrap leading-snug">{e.message}</div>}
                  {e.counter_price != null && (
                    <div className="mt-1.5 pt-1.5 border-t border-current/20 text-xs font-mono">
                      {fr ? 'Contre-proposition' : 'Counter-offer'}: <span className="font-bold">{fmtCur(e.counter_price, e.currency)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2 border-t border-zinc-800 pt-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={fr ? 'Votre message (raison, argument, condition…)' : 'Your message (reason, argument, condition…)'}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm min-h-[70px]"
            data-testid="negotiation-message-input"
            disabled={sending}
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-500 flex-shrink-0">{fr ? 'Contre-proposition (optionnel)' : 'Counter-offer (optional)'}</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={counterPrice}
              onChange={(e) => setCounterPrice(e.target.value)}
              placeholder="0.00"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm max-w-[160px]"
              data-testid="negotiation-counter-input"
              disabled={sending}
            />
            <span className="text-xs text-zinc-500">{currency || 'EUR'}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="negotiation-close-btn">
            {fr ? 'Fermer' : 'Close'}
          </Button>
          <Button onClick={submit} disabled={sending} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold" data-testid="negotiation-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            {fr ? 'Envoyer' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
